/**
 * This function checks if we have the stat returns it, otherwise returns null
 *
 * @param game The event we are getting the stats from
 * @param teamKey The homeTeam/awayTeam stats object to get the stat from
 * @param statKey The stat we are checking for
 * @returns that value of the stat or null
 */
const getTeamStatData = (game: any, teamKey: "awayTeam" | "homeTeam", statKey: string) => {
  if (game[teamKey] && game[teamKey].teamStats && game[teamKey].teamStats[statKey] !== undefined) {
    return game[teamKey].teamStats[statKey];
  }
  return null;
};

/**
 * This function allows us to convert a data point into display value that is ready for the
 * user to view. This will ensure that we never show a bad value to the user.
 *
 * @param stat {number} The data point we are going to set to a string
 */
const getTeamStatDisplay = (stat: number | null, prefix?: string, suffix?: string) => {
  if (stat === null) {
    return "--";
  }
  return `${prefix || ""}${stat}${suffix || ""}`;
};

/**
 * This function creates the teamStats properties object off of the game object whether we have a value
 * or not.
 *
 * @param game The event we are getting stats from
 */
export const getTeamStats = (game: any) => {
  const retValue: any = {};
  // Away team total record
  retValue.statAwayTeamUndersData = getTeamStatData(game, "awayTeam", "unders");
  retValue.statAwayTeamOversData = getTeamStatData(game, "awayTeam", "overs");
  retValue.statAwayTeamPushesData = getTeamStatData(game, "awayTeam", "pushes");
  retValue.statAwayTeamUnderPercentData = getTeamStatData(game, "awayTeam", "underPercent");
  retValue.statAwayTeamOverPercentData = getTeamStatData(game, "awayTeam", "overPercent");
  retValue.statAwayTeamUndersDisplay = getTeamStatDisplay(retValue.statAwayTeamUndersData);
  retValue.statAwayTeamOversDisplay = getTeamStatDisplay(retValue.statAwayTeamOversData);
  retValue.statAwayTeamPushesDisplay = getTeamStatDisplay(retValue.statAwayTeamPushesData);
  retValue.statAwayTeamUnderPercentDisplay =
    retValue.statAwayTeamUnderPercentData !== "--" ? `${Math.trunc(retValue.statAwayTeamUnderPercentData)}%` : "--";
  retValue.statAwayTeamOverPercentDisplay =
    retValue.statAwayTeamOverPercentData !== "--" ? `${Math.trunc(retValue.statAwayTeamOverPercentData)}%` : "--";
  // Home team total record
  retValue.statHomeTeamUndersData = getTeamStatData(game, "homeTeam", "unders");
  retValue.statHomeTeamOversData = getTeamStatData(game, "homeTeam", "overs");
  retValue.statHomeTeamPushesData = getTeamStatData(game, "homeTeam", "pushes");
  retValue.statHomeTeamUnderPercentData = getTeamStatData(game, "homeTeam", "underPercent");
  retValue.statHomeTeamOverPercentData = getTeamStatData(game, "homeTeam", "overPercent");
  retValue.statHomeTeamUndersDisplay = getTeamStatDisplay(retValue.statHomeTeamUndersData);
  retValue.statHomeTeamOversDisplay = getTeamStatDisplay(retValue.statHomeTeamOversData);
  retValue.statHomeTeamPushesDisplay = getTeamStatDisplay(retValue.statHomeTeamPushesData);
  retValue.statHomeTeamUnderPercentDisplay =
    retValue.statHomeTeamUnderPercentData !== "--" ? `${Math.trunc(retValue.statHomeTeamUnderPercentData)}%` : "--";
  retValue.statHomeTeamOverPercentDisplay =
    retValue.statHomeTeamOverPercentData !== "--" ? `${Math.trunc(retValue.statHomeTeamOverPercentData)}%` : "--";
  // Away Team record
  retValue.statAwayTeamWinsData = getTeamStatData(game, "awayTeam", "wins");
  retValue.statAwayTeamLossesData = getTeamStatData(game, "awayTeam", "losses");
  retValue.statAwayTeamTiesData = getTeamStatData(game, "awayTeam", "suties");
  retValue.statAwayTeamGamesData = getTeamStatData(game, "awayTeam", "games");
  retValue.statAwayTeamWinsDisplay = getTeamStatDisplay(retValue.statAwayTeamWinsData);
  retValue.statAwayTeamLossesDisplay = getTeamStatDisplay(retValue.statAwayTeamLossesData);
  retValue.statAwayTeamGamesDisplay = getTeamStatDisplay(retValue.statAwayTeamGamesData);
  retValue.statAwayTeamWinPercentData =
    retValue.statAwayTeamGamesData !== null && retValue.statAwayTeamGamesData > 0
      ? (retValue.statAwayTeamWinsData / retValue.statAwayTeamGamesData) * 100
      : null;
  retValue.statAwayTeamWinPercentDisplay =
    retValue.statAwayTeamWinPercentData !== null ? `${Math.trunc(retValue.statAwayTeamWinPercentData)}%` : "--";
  // Home Team record
  retValue.statHomeTeamWinsData = getTeamStatData(game, "homeTeam", "wins");
  retValue.statHomeTeamLossesData = getTeamStatData(game, "homeTeam", "losses");
  retValue.statHomeTeamTiesData = getTeamStatData(game, "homeTeam", "suties");
  retValue.statHomeTeamGamesData = getTeamStatData(game, "homeTeam", "games");
  retValue.statHomeTeamWinsDisplay = getTeamStatDisplay(retValue.statHomeTeamWinsData);
  retValue.statHomeTeamLossesDisplay = getTeamStatDisplay(retValue.statHomeTeamLossesData);
  retValue.statHomeTeamGamesDisplay = getTeamStatDisplay(retValue.statHomeTeamGamesData);
  retValue.statHomeTeamWinPercentData =
    retValue.statHomeTeamGamesData !== null && retValue.statHomeTeamGamesData > 0
      ? (retValue.statHomeTeamWinsData / retValue.statHomeTeamGamesData) * 100
      : null;
  retValue.statHomeTeamWinPercentDisplay =
    retValue.statHomeTeamWinPercentData !== null ? `${Math.trunc(retValue.statHomeTeamWinPercentData)}%` : "--";
  // Away team spread record
  retValue.statAwayTeamATSWinsData = getTeamStatData(game, "awayTeam", "atswins");
  retValue.statAwayTeamATSLossesData = getTeamStatData(game, "awayTeam", "atslosses");
  retValue.statAwayTeamATSTiesData = getTeamStatData(game, "awayTeam", "atsties");
  retValue.statAwayTeamATSWinPercentData = getTeamStatData(game, "awayTeam", "atsWinPercent");
  retValue.statAwayTeamATSWinsDisplay = getTeamStatDisplay(retValue.statAwayTeamATSWinsData);
  retValue.statAwayTeamATSLossesDisplay = getTeamStatDisplay(retValue.statAwayTeamATSLossesData);
  retValue.statAwayTeamATSTiesDisplay = getTeamStatDisplay(retValue.statAwayTeamATSTiesData);
  retValue.statAwayTeamATSWinPercentDisplay =
    retValue.statAwayTeamATSWinPercentData !== "--" ? `${Math.trunc(retValue.statAwayTeamATSWinPercentData)}%` : "--";
  // Home team spread record
  retValue.statHomeTeamATSWinsData = getTeamStatData(game, "homeTeam", "atswins");
  retValue.statHomeTeamATSLossesData = getTeamStatData(game, "homeTeam", "atslosses");
  retValue.statHomeTeamATSTiesData = getTeamStatData(game, "homeTeam", "atsties");
  retValue.statHomeTeamATSWinPercentData = getTeamStatData(game, "homeTeam", "atsWinPercent");
  retValue.statHomeTeamATSWinsDisplay = getTeamStatDisplay(retValue.statHomeTeamATSWinsData);
  retValue.statHomeTeamATSLossesDisplay = getTeamStatDisplay(retValue.statHomeTeamATSLossesData);
  retValue.statHomeTeamATSTiesDisplay = getTeamStatDisplay(retValue.statHomeTeamATSTiesData);
  retValue.statHomeTeamATSWinPercentDisplay =
    retValue.statHomeTeamATSWinPercentData !== "--" ? `${Math.trunc(retValue.statHomeTeamATSWinPercentData)}%` : "--";
  // Away team units
  retValue.statAwayTeamNetUnitsData = getTeamStatData(game, "awayTeam", "netUnits");
  retValue.statAwayTeamATSUnitsData = getTeamStatData(game, "awayTeam", "atsUnits");
  retValue.statAwayTeamNetUnitsDisplay = getTeamStatDisplay(retValue.statAwayTeamNetUnitsData);
  retValue.statAwayTeamATSUnitsDisplay = getTeamStatDisplay(retValue.statAwayTeamATSUnitsData);
  // Home team units
  retValue.statHomeTeamNetUnitsData = getTeamStatData(game, "homeTeam", "netUnits");
  retValue.statHomeTeamATSUnitsData = getTeamStatData(game, "homeTeam", "atsUnits");
  retValue.statHomeTeamNetUnitsDisplay = getTeamStatDisplay(retValue.statHomeTeamNetUnitsData);
  retValue.statHomeTeamATSUnitsDisplay = getTeamStatDisplay(retValue.statHomeTeamATSUnitsData);
  // Away points
  retValue.statAwayTeamPointsForData = getTeamStatData(game, "awayTeam", "pointsFor");
  retValue.statAwayTeamPointsAgainstData = getTeamStatData(game, "awayTeam", "pointsAgainst");
  retValue.statAwayTeamPointsForDisplay = getTeamStatDisplay(retValue.statAwayTeamPointsForData);
  retValue.statAwayTeamPointsAgainstDisplay = getTeamStatDisplay(retValue.statAwayTeamPointsAgainstData);
  // Home points
  retValue.statHomeTeamPointsForData = getTeamStatData(game, "homeTeam", "pointsFor");
  retValue.statHomeTeamPointsAgainstData = getTeamStatData(game, "homeTeam", "pointsAgainst");
  retValue.statHomeTeamPointsForDisplay = getTeamStatDisplay(retValue.statHomeTeamPointsForData);
  retValue.statHomeTeamPointsAgainstDisplay = getTeamStatDisplay(retValue.statHomeTeamPointsAgainstData);

  ///////////////////////////////////////////////////////////

  // Home - Home Record
  retValue.statHomeTeamHomeWinsData = getTeamStatData(game, "homeTeam", "homeSuWins");
  retValue.statHomeTeamHomeLossesData = getTeamStatData(game, "homeTeam", "homeSuLosses");
  retValue.statHomeTeamHomeTiesData = getTeamStatData(game, "homeTeam", "homeSuTies");
  retValue.statHomeTeamHomeWinsDisplay = getTeamStatDisplay(retValue.statHomeTeamHomeWinsData);
  retValue.statHomeTeamHomeLossesDisplay = getTeamStatDisplay(retValue.statHomeTeamHomeLossesData);
  retValue.statHomeTeamHomeTiesDisplay = getTeamStatDisplay(retValue.statHomeTeamHomeTiesData);
  retValue.statHomeTeamHomeWinPercentageData =
    (retValue.statHomeTeamHomeWinsData /
      (retValue.statHomeTeamHomeWinsData + retValue.statHomeTeamHomeLossesData + retValue.statHomeTeamHomeTiesData)) *
    100;
  // Home - Road Record
  retValue.statHomeTeamRoadWinsData = getTeamStatData(game, "homeTeam", "awaySuWins");
  retValue.statHomeTeamRoadLossesData = getTeamStatData(game, "homeTeam", "awaySuLosses");
  retValue.statHomeTeamRoadTiesData = getTeamStatData(game, "homeTeam", "awaySuTies");
  retValue.statHomeTeamRoadWinsDisplay = getTeamStatDisplay(retValue.statHomeTeamRoadWinsData);
  retValue.statHomeTeamRoadLossesDisplay = getTeamStatDisplay(retValue.statHomeTeamRoadLossesData);
  retValue.statHomeTeamRoadTiesDisplay = getTeamStatDisplay(retValue.statHomeTeamRoadTiesData);
  retValue.statHomeTeamRoadWinPercentageData =
    (retValue.statHomeTeamRoadWinsData /
      (retValue.statHomeTeamRoadWinsData + retValue.statHomeTeamRoadLossesData + retValue.statHomeTeamRoadTiesData)) *
    100;
  // Away - Home Record
  retValue.statAwayTeamHomeWinsData = getTeamStatData(game, "awayTeam", "homeSuWins");
  retValue.statAwayTeamHomeLossesData = getTeamStatData(game, "awayTeam", "homeSuLosses");
  retValue.statAwayTeamHomeTiesData = getTeamStatData(game, "awayTeam", "homeSuTies");
  retValue.statAwayTeamHomeWinsDisplay = getTeamStatDisplay(retValue.statAwayTeamHomeWinsData);
  retValue.statAwayTeamHomeLossesDisplay = getTeamStatDisplay(retValue.statAwayTeamHomeLossesData);
  retValue.statAwayTeamHomeTiesDisplay = getTeamStatDisplay(retValue.statAwayTeamHomeTiesData);
  retValue.statAwayTeamHomeWinPercentageData =
    (retValue.statAwayTeamHomeWinsData /
      (retValue.statAwayTeamHomeWinsData + retValue.statAwayTeamHomeLossesData + retValue.statAwayTeamHomeTiesData)) *
    100;
  // Away - Road Record
  retValue.statAwayTeamRoadWinsData = getTeamStatData(game, "awayTeam", "awaySuWins");
  retValue.statAwayTeamRoadLossesData = getTeamStatData(game, "awayTeam", "awaySuLosses");
  retValue.statAwayTeamRoadTiesData = getTeamStatData(game, "awayTeam", "awaySuTies");
  retValue.statAwayTeamRoadWinsDisplay = getTeamStatDisplay(retValue.statAwayTeamRoadWinsData);
  retValue.statAwayTeamRoadLossesDisplay = getTeamStatDisplay(retValue.statAwayTeamRoadLossesData);
  retValue.statAwayTeamRoadTiesDisplay = getTeamStatDisplay(retValue.statAwayTeamRoadTiesData);
  retValue.statAwayTeamRoadWinPercentageData =
    (retValue.statAwayTeamRoadWinsData /
      (retValue.statAwayTeamRoadWinsData + retValue.statAwayTeamRoadLossesData + retValue.statAwayTeamRoadTiesData)) *
    100;

  ///////////////////////////////////////////////////////////

  // Home - Home ATS Record
  retValue.statHomeTeamHomeATSWinsData = getTeamStatData(game, "homeTeam", "homeAtsWins");
  retValue.statHomeTeamHomeATSLossesData = getTeamStatData(game, "homeTeam", "homeAtsLosses");
  retValue.statHomeTeamHomeATSTiesData = getTeamStatData(game, "homeTeam", "homeAtsTies");
  retValue.statHomeTeamHomeATSWinsDisplay = getTeamStatDisplay(retValue.statHomeTeamHomeATSWinsData);
  retValue.statHomeTeamHomeATSLossesDisplay = getTeamStatDisplay(retValue.statHomeTeamHomeATSLossesData);
  retValue.statHomeTeamHomeATSTiesDisplay = getTeamStatDisplay(retValue.statHomeTeamHomeATSTiesData);
  retValue.statHomeTeamHomeATSWinsPercentageData =
    (retValue.statHomeTeamHomeATSWinsData /
      (retValue.statHomeTeamHomeATSWinsData +
        retValue.statHomeTeamHomeATSLossesData +
        retValue.statHomeTeamHomeATSTiesData)) *
    100;
  // Home - Road ATS Record
  retValue.statHomeTeamRoadATSWinsData = getTeamStatData(game, "homeTeam", "awayAtsWins");
  retValue.statHomeTeamRoadATSLossesData = getTeamStatData(game, "homeTeam", "awayAtsLosses");
  retValue.statHomeTeamRoadATSTiesData = getTeamStatData(game, "homeTeam", "awayAtsTies");
  retValue.statHomeTeamRoadATSWinsDisplay = getTeamStatDisplay(retValue.statHomeTeamRoadATSWinsData);
  retValue.statHomeTeamRoadATSLossesDisplay = getTeamStatDisplay(retValue.statHomeTeamRoadATSLossesData);
  retValue.statHomeTeamRoadATSTiesDisplay = getTeamStatDisplay(retValue.statHomeTeamRoadATSTiesData);
  retValue.statHomeTeamRoadATSWinsPercentageData =
    (retValue.statHomeTeamRoadATSWinsData /
      (retValue.statHomeTeamRoadATSWinsData +
        retValue.statHomeTeamRoadATSLossesData +
        retValue.statHomeTeamRoadATSTiesData)) *
    100;
  // Away - Home ATS Record
  retValue.statAwayTeamHomeATSWinsData = getTeamStatData(game, "awayTeam", "homeAtsWins");
  retValue.statAwayTeamHomeATSLossesData = getTeamStatData(game, "awayTeam", "homeAtsLosses");
  retValue.statAwayTeamHomeATSTiesData = getTeamStatData(game, "awayTeam", "homeAtsTies");
  retValue.statAwayTeamHomeATSWinsDisplay = getTeamStatDisplay(retValue.statAwayTeamHomeATSWinsData);
  retValue.statAwayTeamHomeATSLossesDisplay = getTeamStatDisplay(retValue.statAwayTeamHomeATSLossesData);
  retValue.statAwayTeamHomeATSTiesDisplay = getTeamStatDisplay(retValue.statAwayTeamHomeATSTiesData);
  retValue.statAwayTeamHomeATSWinsPercentageData =
    (retValue.statAwayTeamHomeATSWinsData /
      (retValue.statAwayTeamHomeATSWinsData +
        retValue.statAwayTeamHomeATSLossesData +
        retValue.statAwayTeamHomeATSTiesData)) *
    100;
  // Away - Road ATS Record
  retValue.statAwayTeamRoadATSWinsData = getTeamStatData(game, "awayTeam", "awayAtsWins");
  retValue.statAwayTeamRoadATSLossesData = getTeamStatData(game, "awayTeam", "awayAtsLosses");
  retValue.statAwayTeamRoadATSTiesData = getTeamStatData(game, "awayTeam", "awayAtsTies");
  retValue.statAwayTeamRoadATSWinsDisplay = getTeamStatDisplay(retValue.statAwayTeamRoadATSWinsData);
  retValue.statAwayTeamRoadATSLossesDisplay = getTeamStatDisplay(retValue.statAwayTeamRoadATSLossesData);
  retValue.statAwayTeamRoadATSTiesDisplay = getTeamStatDisplay(retValue.statAwayTeamRoadATSTiesData);
  retValue.statAwayTeamRoadATSWinsPercentageData =
    (retValue.statAwayTeamRoadATSWinsData /
      (retValue.statAwayTeamRoadATSWinsData +
        retValue.statAwayTeamRoadATSLossesData +
        retValue.statAwayTeamRoadATSTiesData)) *
    100;
  ///////////////////////////////////////////////////////////

  // Home - Home O/U Record
  retValue.statHomeTeamHomeOUWinsData = getTeamStatData(game, "homeTeam", "homeOvers");
  retValue.statHomeTeamHomeOULossesData = getTeamStatData(game, "homeTeam", "homeUnders");
  retValue.statHomeTeamHomeOUTiesData = getTeamStatData(game, "homeTeam", "homePushes");
  retValue.statHomeTeamHomeOUWinsDisplay = getTeamStatDisplay(retValue.statHomeTeamHomeOUWinsData);
  retValue.statHomeTeamHomeOULossesDisplay = getTeamStatDisplay(retValue.statHomeTeamHomeOULossesData);
  retValue.statHomeTeamHomeOUTiesDisplay = getTeamStatDisplay(retValue.statHomeTeamHomeOUTiesData);
  retValue.statHomeTeamHomeOUWinsPercentageData =
    (retValue.statHomeTeamHomeOUWinsData /
      (retValue.statHomeTeamHomeOUWinsData +
        retValue.statHomeTeamHomeOULossesData +
        retValue.statHomeTeamHomeOUTiesData)) *
    100;
  // Home - Road O/U Record
  retValue.statHomeTeamRoadOUWinsData = getTeamStatData(game, "homeTeam", "awayOvers");
  retValue.statHomeTeamRoadOULossesData = getTeamStatData(game, "homeTeam", "awayUnders");
  retValue.statHomeTeamRoadOUTiesData = getTeamStatData(game, "homeTeam", "awayPushes");
  retValue.statHomeTeamRoadOUWinsDisplay = getTeamStatDisplay(retValue.statHomeTeamRoadOUWinsData);
  retValue.statHomeTeamRoadOULossesDisplay = getTeamStatDisplay(retValue.statHomeTeamRoadOULossesData);
  retValue.statHomeTeamRoadOUTiesDisplay = getTeamStatDisplay(retValue.statHomeTeamRoadOUTiesData);
  retValue.statHomeTeamRoadOUWinsPercentageData =
    (retValue.statHomeTeamRoadOUWinsData /
      (retValue.statHomeTeamRoadOUWinsData +
        retValue.statHomeTeamRoadOULossesData +
        retValue.statHomeTeamRoadOUTiesData)) *
    100;
  // Away - Home O/U Record
  retValue.statAwayTeamHomeOUWinsData = getTeamStatData(game, "awayTeam", "homeOvers");
  retValue.statAwayTeamHomeOULossesData = getTeamStatData(game, "awayTeam", "homeUnders");
  retValue.statAwayTeamHomeOUTiesData = getTeamStatData(game, "awayTeam", "homePushes");
  retValue.statAwayTeamHomeOUWinsDisplay = getTeamStatDisplay(retValue.statAwayTeamHomeOUWinsData);
  retValue.statAwayTeamHomeOULossesDisplay = getTeamStatDisplay(retValue.statAwayTeamHomeOULossesData);
  retValue.statAwayTeamHomeOUTiesDisplay = getTeamStatDisplay(retValue.statAwayTeamHomeOUTiesData);
  retValue.statAwayTeamHomeOUWinsPercentageData =
    (retValue.statAwayTeamHomeOUWinsData /
      (retValue.statAwayTeamHomeOUWinsData +
        retValue.statAwayTeamHomeOULossesData +
        retValue.statAwayTeamHomeOUTiesData)) *
    100;
  // Away - Road O/U Record
  retValue.statAwayTeamRoadOUWinsData = getTeamStatData(game, "awayTeam", "awayOvers");
  retValue.statAwayTeamRoadOULossesData = getTeamStatData(game, "awayTeam", "awayUnders");
  retValue.statAwayTeamRoadOUTiesData = getTeamStatData(game, "awayTeam", "awayPushes");
  retValue.statAwayTeamRoadOUWinsDisplay = getTeamStatDisplay(retValue.statAwayTeamRoadOUWinsData);
  retValue.statAwayTeamRoadOULossesDisplay = getTeamStatDisplay(retValue.statAwayTeamRoadOULossesData);
  retValue.statAwayTeamRoadOUTiesDisplay = getTeamStatDisplay(retValue.statAwayTeamRoadOUTiesData);
  retValue.statAwayTeamRoadOUWinsPercentageData =
    (retValue.statAwayTeamRoadOUWinsData /
      (retValue.statAwayTeamRoadOUWinsData +
        retValue.statAwayTeamRoadOULossesData +
        retValue.statAwayTeamRoadOUTiesData)) *
    100;

  return retValue;
};
